import React, { useRef, useState, useEffect, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useNavigate } from 'react-router-dom';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import styles from './NewProductLanding.module.css';
import { logEvent } from '../../analytics';
import { useDispatch, useSelector } from 'react-redux';
import { saveStateBeforeNavigation } from '../../store/reducers/progressionReducer';

// Composant 3D de la boîte avec modèle GLB
function LensBox({ scrollProgress, snapRotation, isSnapping }) {
  const boxRef = useRef();
  const targetRotation = useRef(0);
  const currentRotation = useRef(0);
  const materialsRef = useRef([]);

  // Rotation initiale de la boîte (angle de profil)
  const INITIAL_ROTATION = 0.9;

  // Charger le modèle GLB
  const { scene } = useGLTF('/boite.glb');

  // Sauvegarder les matériaux originaux une seule fois
  useEffect(() => {
    if (scene && materialsRef.current.length === 0) {
      scene.traverse((child) => {
        if (child.isMesh && child.material) {
          // Sauvegarder les infos du matériau original
          materialsRef.current.push({
            material: child.material,
            originalColor: child.material.color ? child.material.color.clone() : null,
            originalMap: child.material.map,
            originalEmissive: child.material.emissive ? child.material.emissive.clone() : null
          });
        }
      });
    }
  }, [scene]);

  useFrame((state) => {
    if (boxRef.current) {
      const time = state.clock.getElapsedTime();

      // Calculer la progression de révélation (0-0.15)
      const revealProgress = Math.min(scrollProgress / 0.15, 1);

      // Appliquer la transition blanc -> texture (optimisé, seulement pendant la phase de révélation)
      if (revealProgress < 1 && !isSnapping) {
        materialsRef.current.forEach(({ material, originalColor, originalMap }) => {
          if (material.color && originalColor) {
            // Interpoler entre blanc et couleur originale
            material.color.lerpColors(
              new THREE.Color(1, 1, 1),
              originalColor,
              revealProgress
            );
          }

          // Gérer la texture
          if (originalMap) {
            material.map = originalMap;
            // Ajuster l'opacité de la texture si le matériau le supporte
            if (material.opacity !== undefined) {
              material.opacity = 0 + revealProgress * 2.8;
              material.transparent = true;
            }
          }
        });
      } else if (revealProgress >= 1 && !isSnapping) {
        // Une fois la révélation terminée, restaurer les matériaux originaux
        materialsRef.current.forEach(({ material, originalColor, originalMap }) => {
          if (material.color && originalColor) {
            material.color.copy(originalColor);
          }
          if (originalMap) {
            material.map = originalMap;
            material.opacity = 1;
            material.transparent = false;
          }
        });
      }

      // Si on est en mode snap, priorité à la rotation du snap
      if (isSnapping) {
        // Mode snap : la boîte reste fixe et tourne selon le scroll
        // On ajoute la rotation initiale pour conserver l'angle de profil
        boxRef.current.scale.set(1.2, 1.2, 1.2); // Taille max
        boxRef.current.position.y = 0; // Position fixe
        targetRotation.current = INITIAL_ROTATION + snapRotation;
        currentRotation.current = targetRotation.current; // Mise à jour directe en mode snap
        boxRef.current.rotation.y = currentRotation.current;

      }
      // Phase 1 (0-0.15): Fixe au centre, croissance + révélation
      else if (scrollProgress < 0.15) {
        const growProgress = scrollProgress / 0.15;
        const scale = 0.5 + growProgress * 0.7; // De 0.5 à 1.2 (commence plus petit)
        boxRef.current.scale.set(scale, scale, scale);
        boxRef.current.position.y = 0;

        // Rotation de base avec lerp fluide
        targetRotation.current = INITIAL_ROTATION;
        currentRotation.current += (targetRotation.current - currentRotation.current) * 0.1;
        boxRef.current.rotation.y = currentRotation.current;
      }
      // Phase 2 (0.15-0.25): Disparition RAPIDE mais fluide sur une courte plage
      else if (scrollProgress < 0.25) {
        const descendProgress = (scrollProgress - 0.15) / 0.10; // Plage de 10% du scroll total
        // Courbe d'accélération douce (ease-out) pour transition fluide
        const smoothProgress = 1 - Math.pow(1 - descendProgress, 3);

        // Diminution de taille progressive mais rapide
        const scale = Math.max(0.01, 1.2 - smoothProgress * 1.19);
        boxRef.current.scale.set(scale, scale, scale);

        // Descente rapide avec courbe d'accélération
        boxRef.current.position.y = -smoothProgress * 2;

        // Rotation très subtile avec lerp pour transition smooth
        targetRotation.current = INITIAL_ROTATION + smoothProgress * Math.PI * 0.3;
        currentRotation.current += (targetRotation.current - currentRotation.current) * 0.1;
        boxRef.current.rotation.y = currentRotation.current;
      }
      // Phase 3 (après 0.25): Complètement invisible et hors écran
      else {
        const scale = Math.max(0.001, boxRef.current.scale.x * 0.95); // Diminution progressive
        boxRef.current.scale.set(scale, scale, scale);
        boxRef.current.position.y += -0.5; // Descente progressive

        targetRotation.current = INITIAL_ROTATION;
        currentRotation.current += (targetRotation.current - currentRotation.current) * 0.05;
        boxRef.current.rotation.y = currentRotation.current;
      }

      // Flottement très léger continu (sauf en mode snap)
      if (!isSnapping) {
        boxRef.current.position.x = Math.sin(time * 0.3) * 0.05;
        boxRef.current.rotation.z = Math.sin(time * 0.2) * 0.02;
      }
    }
  });

  return (
    <group ref={boxRef} position={[0, 0, 0]}>
      {/* Modèle GLB avec textures */}
      <primitive
        object={scene.clone()}
        castShadow
        receiveShadow
        scale={20.5}
        rotation={[Math.PI / 2, 0, 0]}
      />
    </group>
  );
}

// Précharger le modèle GLB
useGLTF.preload('/boite.glb');

// Scene 3D minimaliste et élégante
function Scene({ scrollProgress, snapRotation, isSnapping }) {
  return (
    <>
      {/* Éclairage pour la boîte */}
      <ambientLight intensity={0.8} />

      {/* Lumières directionnelles pour éclairer la boîte */}
      <directionalLight
        position={[5, 5, 5]}
        intensity={1}
        castShadow
      />
      <directionalLight
        position={[-3, 2, -3]}
        intensity={0.5}
      />

      {/* La boîte */}
      <LensBox scrollProgress={scrollProgress} snapRotation={snapRotation} isSnapping={isSnapping} />
    </>
  );
}

export default function NewProductLanding() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [boxRotation, setBoxRotation] = useState(0.9);
  const [snapTextIndex, setSnapTextIndex] = useState(0);
  const [showSnapText, setShowSnapText] = useState(false);
  const [isSnapping, setIsSnapping] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const containerRef = useRef();
  const snapScrollAccumulator = useRef(0);
  const hasTriggeredSnap = useRef(false);
  const touchStartY = useRef(0);
  const lastTouchY = useRef(0);

  useEffect(() => {
    logEvent('page_view', 'new_product_landing');
    
    // Sauvegarder l'état actuel de la caméra/scène avant d'afficher cette page
    dispatch(saveStateBeforeNavigation());
    
    // Cleanup complet au démontage du composant
    return () => {
      // S'assurer que le scroll est débloqué
      document.body.style.overflow = '';
      // Scroll en haut de page pour éviter les problèmes
      window.scrollTo(0, 0);
    };
  }, [dispatch]);

  // Détecter le changement de taille d'écran
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Textes à afficher pendant le snap
  const snapTexts = [
    "A unique",
    "ready to inject",
    "Swiss innovation"
  ];

  // Gestion du scroll avec zone de snap
  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const scrollTop = window.scrollY;
        const windowHeight = window.innerHeight;
        const docHeight = document.documentElement.scrollHeight;

        // Calculer la progression du scroll (0 à 1)
        const maxScroll = docHeight - windowHeight;
        const progress = Math.min(scrollTop / maxScroll, 1);

        if (!isSnapping) {
          setScrollProgress(progress);

          // Zone de snap : section intro (entre 1vh et 2vh de scroll)
          // Sur mobile, on rend la zone de snap optionnelle pour éviter les blocages
          const introStartScroll = windowHeight * 1.0;
          const introEndScroll = windowHeight * 2.0;

          if (!hasTriggeredSnap.current &&
            scrollTop >= introStartScroll &&
            scrollTop < introEndScroll &&
            !isMobile) { // Désactiver le snap automatique sur mobile
            // Entrer dans la zone de snap sur la section intro
            hasTriggeredSnap.current = true;
            setIsSnapping(true);
            document.body.style.overflow = 'hidden';
            snapScrollAccumulator.current = 0;
            setBoxRotation(0);
            setSnapTextIndex(0);
            setShowSnapText(true); // Montrer le premier texte immédiatement
          }

          // Réinitialiser le flag si on remonte avant la section intro
          if (scrollTop < introStartScroll * 0.9) {
            hasTriggeredSnap.current = false;
          }
        }
      }
    };

    const handleWheel = (e) => {
      if (isSnapping) {
        e.preventDefault();

        // Accumuler le scroll dans la zone de snap - sensibilité réduite pour contrôle plus fin
        snapScrollAccumulator.current += e.deltaY * 0.004;

        // Faire tourner la boîte directement (pas de multiplication)
        setBoxRotation(snapScrollAccumulator.current);

        console.log('Snap rotation:', snapScrollAccumulator.current, 'radians =', (snapScrollAccumulator.current / Math.PI * 180).toFixed(0), '°');

        // Afficher les textes en fonction de la rotation (un tour complet = 2π)
        // Diviser le tour en 3 parties pour les 3 textes
        const absoluteRotation = Math.abs(snapScrollAccumulator.current);
        const textIndex = Math.floor((absoluteRotation / (Math.PI * 2)) * 3) % 3;

        if (textIndex !== snapTextIndex) {
          setSnapTextIndex(textIndex);
          setShowSnapText(true);

          // Les textes restent visibles plus longtemps (1500ms au lieu de 800ms)
          setTimeout(() => {
            setShowSnapText(false);
          }, 1500);
        }

        // Si l'utilisateur a fait un tour complet (2π), sortir automatiquement
        if (absoluteRotation >= Math.PI * 2) {
          console.log('Tour complet ! Sortie du snap...');
          // Transition douce en sortie
          setIsSnapping(false);
          setShowSnapText(false);

          // Débloquer le scroll
          document.body.style.overflow = '';

          const scrollTop = window.scrollY;
          const windowHeight = window.innerHeight;

          // Scroll vers le bas avec transition douce
          window.scrollTo({ top: scrollTop + windowHeight * 0.3, behavior: 'smooth' });

          // Reset simple - la rotation sera gérée par le lerp dans useFrame
          snapScrollAccumulator.current = 0;
          setSnapTextIndex(0);
        } else if (snapScrollAccumulator.current < -Math.PI * 0.2) {
          // Si scroll arrière, sortir (seuil réduit pour faciliter la sortie)
          console.log('Scroll arrière, sortie du snap...');
          setIsSnapping(false);
          setShowSnapText(false);
          document.body.style.overflow = '';

          // Reset simple
          snapScrollAccumulator.current = 0;
          setSnapTextIndex(0);
        }
      }
    };

    // Gestion tactile pour mobile (touchstart)
    const handleTouchStart = (e) => {
      if (isSnapping) {
        touchStartY.current = e.touches[0].clientY;
        lastTouchY.current = e.touches[0].clientY;
      }
    };

    // Gestion tactile pour mobile (touchmove)
    const handleTouchMove = (e) => {
      if (isSnapping) {
        e.preventDefault();

        const currentTouchY = e.touches[0].clientY;
        const deltaY = lastTouchY.current - currentTouchY;
        lastTouchY.current = currentTouchY;

        // Accumuler le mouvement - sensibilité adaptée pour tactile
        snapScrollAccumulator.current += deltaY * 0.008;

        // Faire tourner la boîte
        setBoxRotation(snapScrollAccumulator.current);

        console.log('Snap rotation (touch):', snapScrollAccumulator.current, 'radians =', (snapScrollAccumulator.current / Math.PI * 180).toFixed(0), '°');

        // Afficher les textes en fonction de la rotation
        const absoluteRotation = Math.abs(snapScrollAccumulator.current);
        const textIndex = Math.floor((absoluteRotation / (Math.PI * 2)) * 3) % 3;

        if (textIndex !== snapTextIndex) {
          setSnapTextIndex(textIndex);
          setShowSnapText(true);

          setTimeout(() => {
            setShowSnapText(false);
          }, 1500);
        }

        // Si l'utilisateur a fait un tour complet (2π), sortir automatiquement
        if (absoluteRotation >= Math.PI * 2) {
          console.log('Tour complet (touch) ! Sortie du snap...');
          setIsSnapping(false);
          setShowSnapText(false);
          document.body.style.overflow = '';

          const scrollTop = window.scrollY;
          const windowHeight = window.innerHeight;

          window.scrollTo({ top: scrollTop + windowHeight * 0.3, behavior: 'smooth' });

          snapScrollAccumulator.current = 0;
          setSnapTextIndex(0);
        } else if (snapScrollAccumulator.current < -Math.PI * 0.15) { // Seuil réduit pour mobile (0.15 au lieu de 0.3)
          console.log('Scroll arrière (touch), sortie du snap...');
          setIsSnapping(false);
          setShowSnapText(false);
          document.body.style.overflow = '';

          snapScrollAccumulator.current = 0;
          setSnapTextIndex(0);
        }
      }
    };

    // Gestion tactile pour mobile (touchend)
    const handleTouchEnd = () => {
      if (isSnapping) {
        touchStartY.current = 0;
        lastTouchY.current = 0;
      }
    };

    // Sécurité : débloquer le scroll après 5 secondes sur mobile, 10 secondes sur desktop
    const safetyTimeout = setTimeout(() => {
      if (isSnapping) {
        console.log('Safety timeout: déblocage du scroll');
        setIsSnapping(false);
        setShowSnapText(false);
        document.body.style.overflow = '';
        setBoxRotation(0);
        snapScrollAccumulator.current = 0;
      }
    }, isMobile ? 5000 : 10000);

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      clearTimeout(safetyTimeout);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      document.body.style.overflow = '';
    };
  }, [isSnapping, isMobile, snapTextIndex]);

  const handleBackToHome = () => {
    logEvent('ui_interaction', 'back_to_home_click');
    
    // Nettoyer l'état local avant de naviguer
    setIsSnapping(false);
    setShowSnapText(false);
    document.body.style.overflow = '';
    window.scrollTo(0, 0);
    setScrollProgress(0);
    setBoxRotation(0.9);
    snapScrollAccumulator.current = 0;
    hasTriggeredSnap.current = false;
    
    // Définir le flag pour indiquer qu'on revient vers la page principale
    // Cela permettra de restaurer la position sauvegardée
    sessionStorage.setItem('saviol-returning-from-new-product', 'true');
    
    // L'état sera restauré automatiquement par MainPage au montage
    navigate('/');
  };

  const handleEscapeSnap = () => {
    console.log('Escape snap manually');
    setIsSnapping(false);
    setShowSnapText(false);
    document.body.style.overflow = '';
    setBoxRotation(0);
    snapScrollAccumulator.current = 0;
    setSnapTextIndex(0);
  };

  // ESC pour sortir du snap
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isSnapping) {
        handleEscapeSnap();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSnapping]);


  // Calculer la couleur de fond en fonction du scroll
  const getBackgroundColor = () => {
    const transitionProgress = Math.min(scrollProgress / 0.2, 1);
    const r = Math.round(54 + (250 - 54) * transitionProgress);
    const g = Math.round(98 + (250 - 98) * transitionProgress);
    const b = Math.round(97 + (250 - 97) * transitionProgress);
    return `rgb(${r}, ${g}, ${b})`;
  };

  return (
    <div
      className={styles.container}
      ref={containerRef}
      style={{ backgroundColor: getBackgroundColor() }}
    >
      {/* Canvas 3D fixe en arrière-plan */}
      <div className={styles.canvasContainer}>
        <Canvas
          camera={{ position: [0, 0, 6], fov: 45 }}
          shadows
          gl={{
            antialias: true,
            alpha: true
          }}
        >
          <Suspense fallback={null}>
            <Scene
              scrollProgress={scrollProgress}
              snapRotation={boxRotation}
              isSnapping={isSnapping}
            />
          </Suspense>
        </Canvas>
      </div>

      {/* Bouton retour */}
      <button className={styles.backButton} onClick={handleBackToHome}>
        ← Back to Swiss Virtual Clinic
      </button>

      {/* Indicateur de scroll (visible uniquement en haut de page) */}
      {scrollProgress < 0.1 && (
        <div className={styles.scrollIndicator}>
          <span>Scroll to discover</span>
        </div>
      )}

      {/* Image flottante qui descend */}
      {!isSnapping && scrollProgress < 0.15 && (
        <div
          className={styles.floatingImage}
          style={{
            transform: `translateY(${(isMobile ? 20 : -25) + scrollProgress * -500}vh)`,
            opacity: 1 - (scrollProgress / 0.15) * 0.7
          }}
        >
          <img src="/Inno_2026_teasing.png" alt="Product teasing" />
        </div>
      )}

      {/* Textes qui apparaissent pendant le snap */}
      {showSnapText && (
        <div className={styles.snapTextOverlay}>
          <h2 className={styles.snapText}>
            {snapTexts[snapTextIndex]}
          </h2>
        </div>
      )}

      {/* Bouton pour sortir du snap sur mobile */}
      {isSnapping && isMobile && (
        <button className={styles.skipSnapButton} onClick={handleEscapeSnap}>
          Skip ↓
        </button>
      )}

      {/* Contenu de la landing page - Style Euveka */}
      <div className={styles.content}>
        {/* Section Hero - Grande typographie */}
        <section className={styles.hero}>
          <div className={styles.heroContent}>
            <div className={styles.preTitle}>Swiss Advanced Vision</div>
            <h1 className={styles.mainTitle}>
              <span className={styles.titleLine1}>New Fully Preloaded</span>
              <span className={styles.titleLine2}>Advanced EDOF IOL</span>
            </h1>
            <div className={styles.scrollPrompt}>
              <span>SCROLL TO REVEAL</span>
              <div className={styles.scrollLine}></div>
            </div>
          </div>
        </section>

        {/* Section Intro - Espace pour le snap (pas de texte ici) */}
        <section className={styles.intro}>
          {/* Les textes apparaissent via snapTextOverlay pendant le snap */}

        </section>

        {/* Section Stats - Style Euveka */}
        <section className={styles.stats}>

          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>2026</div>
              <div className={styles.statLabel}>Launch Year*</div>
              <div className={styles.statSublabel}>*pending notified body release</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>100%</div>
              <div className={styles.statLabel}>Swiss Made</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>Innovation</div>
              <div className={styles.statLabel}>Exclusively presented at ESCRS 2025</div>
            </div>
          </div>
        </section>

        {/* Section Description avec chiffres */}
        <section className={styles.description}>
          <div className={styles.descContent}>
            <p className={styles.descText}>
              Featuring <strong>Advanced Instant Focus Technology</strong>,
              delivering continuous sharp vision at all distances.
              A unique ready-to-inject solution that combines
              <strong> Swiss precision</strong> with performance.
            </p>
          </div>
        </section>

        {/* Section Features en grille - Style Euveka */}
        <section className={styles.features}>
          <h2 className={styles.featuresTitle}>The Experience</h2>
          <div className={styles.featuresList}>
            <div className={styles.featureItem}>
              <div className={styles.featureNumber}>01 — 03</div>
              <h3 className={styles.featureTitle}>Higher Performance</h3>
              <p className={styles.featureText}>
                Continuous sharp vision at all distances, including in low-light
                conditions. The advanced optical design ensures exceptional visual
                quality for your patients.
              </p>
            </div>

            <div className={styles.featureItem}>
              <div className={styles.featureNumber}>02 — 03</div>
              <h3 className={styles.featureTitle}>Optimized Size</h3>
              <p className={styles.featureText}>
                A single-size IOL design developed based on extensive clinical
                experience. Simplifies inventory management while maintaining
                superior outcomes.
              </p>
            </div>

            <div className={styles.featureItem}>
              <div className={styles.featureNumber}>03 — 03</div>
              <h3 className={styles.featureTitle}>Improved Usability</h3>
              <p className={styles.featureText}>
                Innovative Fully Preloaded Solution designed for enhanced safety
                and ease of use. Reduces surgical time and minimizes risk of
                contamination.
              </p>
            </div>
          </div>
        </section>

        {/* Section Standards - Style Euveka */}
        <section className={styles.standards}>
          <h2 className={styles.standardsTitle}>
            <span className={styles.pantoneColor}>Our</span> <span>Standards</span>
            <br />
            <span>Meet</span> <span className={styles.pantoneColor}>Yours</span>
          </h2>
          <div className={styles.standardsGrid}>
            <div className={styles.standardItem}>
              <h4>Flawless Finishes</h4>
              <p>Precision engineering and meticulous attention to detail</p>
            </div>
            <div className={styles.standardItem}>
              <h4>Refined Aesthetics</h4>
              <p>Modern preloaded design that elevates surgical workflow</p>
            </div>
            <div className={styles.standardItem}>
              <h4>High-End Materials</h4>
              <p>Premium-grade hydrophilic acrylic built for performance</p>
            </div>
          </div>
        </section>

        {/* Section Image produit - Grille d'images */}
        <section className={styles.productImage}>
          <div className={styles.portfolioGrid}>
            <div className={styles.portfolioItem}>
              <img
                src="/portfolio-1.jpg"
                alt="2026 Innovation - Preloaded EDOF IOL"
                className={styles.productPhoto}
                loading="eager"
              />
            </div>
            <div className={styles.portfolioItem}>
              <img
                src="/portfolio-2.jpg"
                alt="LUCIDIS - Full Range Vision"
                className={styles.productPhoto}
                loading="eager"
              />
            </div>
            <div className={styles.portfolioItem}>
              <img
                src="/portfolio-3.jpg"
                alt="EDEN - Near-Intermediate Vision"
                className={styles.productPhoto}
                loading="eager"
              />
            </div>
          </div>
          <div className={styles.imageOverlay}>
            <p className={styles.imageTitle}>Already experience our technology in your
              practice with <a href="https://sav-iol.com/product-eden/" target="_blank">Eden</a> & <a href="https://sav-iol.com/product-lucidis/" target="_blank">Lucidis</a></p>
          </div>
        </section>

        {/* Section Bénéfices - Style liste 
        <section className={styles.benefits}>
          <h2 className={styles.benefitsTitle}>Details you will love</h2>
          <div className={styles.benefitsGrid}>
            <div className={styles.benefitItem}>
              <h4>Ships Fast</h4>
              <p>Available within standard delivery timeframes</p>
            </div>
            <div className={styles.benefitItem}>
              <h4>Always Connected</h4>
              <p>Stay tuned via Swiss Virtual Clinic</p>
            </div>
            <div className={styles.benefitItem}>
              <h4>We've Got You Covered</h4>
              <p>Comprehensive support and training materials</p>
            </div>
            <div className={styles.benefitItem}>
              <h4>Fully Equipped</h4>
              <p>Complete preloaded solution ready to inject</p>
            </div>
            <div className={styles.benefitItem}>
              <h4>Award Winning Product</h4>
              <p>Swiss precision meets innovation</p>
            </div>
            <div className={styles.benefitItem}>
              <h4>Plug & Play</h4>
              <p>Seamless integration into your workflow</p>
            </div>
          </div>
        </section>

        {/* Section CTA finale */}
        <section className={styles.ctaFinal}>
          <h2 className={styles.ctaTitle}>
            <span>Ready</span>
            <br />
            <span>to Evolve?</span>
          </h2>
          <p className={styles.ctaSubtext}>
            Stay tuned for exclusive updates available on Swiss Virtual Clinic.
            This page will be gradually updated with new information until the
            global launch in 2026.
          </p>
        </section>

        {/* Footer */}
        <footer className={styles.footer}>
          <p>© 2026 SAV-IOL - Swiss Advanced Vision</p>
          <p>
            <a href="mailto:info@sav-iol.com">info@sav-iol.com</a> |
            <a href="tel:+41325665400"> +41 32 566 54 00</a>
          </p>
        </footer>
      </div>
    </div>
  );
}
